import { Link } from "react-router"
import { ArrowRight, Wifi, Image, BarChart3, Star, CheckCircle2, Zap, Eye } from "lucide-react"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card } from "@/components/ui/card"
import { Separator } from "@/components/ui/separator"

/* ─── Data ─── */

const BEFORE_AFTER = [
  {
    before: "Revenue data scattered across 3 platforms — no unified view",
    after: "Single dashboard with revenue, pace, ADR, occupancy, and channel mix",
  },
  {
    before: "Guest reviews read one at a time, no trend analysis",
    after: "Unified reputation score across platforms with sentiment tracking",
  },
  {
    before: "Listing content updated when it \"felt\" outdated",
    after: "Seasonal relevance scoring that flags when content drifts from demand",
  },
  {
    before: "Pricing decisions based on competitor spot-checks",
    after: "Revenue intelligence with pace vs. comparison periods and confidence ranges",
  },
  {
    before: "No visibility into what guests actually value most",
    after: "Review analysis showing top strengths, emerging risks, and loyalty signals",
  },
  {
    before: "Guest preparation was manual checklists and memory",
    after: "Stay readiness scoring with automated preparation tracking",
  },
]

const FEATURES = [
  {
    name: "Performance",
    description: "Revenue, pace, ADR, occupancy, channel mix, lead times, and forecast — with comparison periods and confidence ranges.",
    icon: BarChart3,
  },
  {
    name: "Reputation",
    description: "Unified score across Airbnb and Booking.com with sentiment trends, strength/friction analysis, and loyalty signals.",
    icon: Star,
  },
  {
    name: "Listings",
    description: "Content audit with seasonal relevance scoring, photo gap analysis, and AI-generated improvement suggestions.",
    icon: Image,
  },
  {
    name: "Guest Experience",
    description: "Stay readiness checks, preparation tracking, and guest-facing content orchestration.",
    icon: CheckCircle2,
  },
  {
    name: "Action Rail",
    description: "Prioritised, confidence-ranked recommendations based on all your data — what to fix first, and why.",
    icon: Zap,
  },
]

const TAKEAWAYS = [
  "Reputation intelligence caught a WiFi issue trending across two platforms — invisible when reading reviews one at a time",
  "Listing audit revealed seasonal content mismatch and underrepresented guest-validated features",
  "Action rail provided prioritised, confidence-ranked recommendations — not just dashboards",
  "Setup took under ten minutes with an existing Lodgify account",
]

/* ─── Components ─── */

function Quote({ children }: { children: React.ReactNode }) {
  return (
    <blockquote className="border-l-4 border-primary/40 pl-5 py-1 my-6">
      <p className="text-[15px] leading-relaxed text-muted-foreground italic">
        {children}
      </p>
    </blockquote>
  )
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex items-center gap-2 mb-1">
      <span className="text-xs font-semibold tracking-wider uppercase text-primary">
        {children}
      </span>
    </div>
  )
}

/* ─── Page ─── */

export function CaseStudyPage() {
  return (
    <div className="min-h-screen bg-background">
      {/* ── Top bar ── */}
      <header className="sticky top-0 z-50 border-b border-border/50 bg-background/80 backdrop-blur-sm">
        <div className="mx-auto max-w-3xl flex items-center justify-between px-6 h-14">
          <Link to="/" className="text-sm font-semibold text-primary tracking-tight">
            Stayframe
          </Link>
          <div className="flex items-center gap-3">
            <Link to="/demo">
              <Button variant="ghost" size="sm">
                <Eye className="h-4 w-4 mr-2" />
                See demo
              </Button>
            </Link>
            <Link to="/sign-up">
              <Button size="sm">
                Start free trial
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* ── Hero ── */}
      <section className="mx-auto max-w-3xl px-6 pt-16 pb-12">
        <Badge variant="outline" className="mb-6 text-xs">Case Study</Badge>
        <h1 className="text-3xl font-medium font-display tracking-tight mb-4 text-foreground">
          "I Thought I Knew My Properties.<br />
          Stayframe Showed Me What I Was Missing."
        </h1>
        <p className="text-[15px] leading-relaxed text-muted-foreground max-w-xl">
          How one coastal host used property intelligence to prepare for spring season — and stopped flying blind.
        </p>
      </section>

      <Separator className="mx-auto max-w-3xl" />

      {/* ── Snapshot ── */}
      <section className="mx-auto max-w-3xl px-6 py-10">
        <Card className="bg-teal-50/50 border-primary/20 p-6">
          <SectionLabel>At a Glance</SectionLabel>
          <dl className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3 text-sm">
            {[
              ["Host profile", "Independent host, coastal vacation rentals in southern Europe"],
              ["Portfolio", "Small portfolio (fewer than 5 properties)"],
              ["PMS", "Lodgify"],
              ["Trial period", "4 weeks (early access)"],
              ["Key outcome", "Caught a listing content mismatch and pricing weakness ahead of spring season"],
            ].map(([label, value]) => (
              <div key={label} className="flex flex-col">
                <dt className="text-xs font-semibold text-foreground">{label}</dt>
                <dd className="text-muted-foreground leading-relaxed">{value}</dd>
              </div>
            ))}
          </dl>
        </Card>
      </section>

      {/* ── The Challenge ── */}
      <article className="mx-auto max-w-3xl px-6 space-y-10 pb-16">
        <section>
          <SectionLabel>The Blind Spot Problem</SectionLabel>
          <h2 className="text-[17px] font-semibold leading-snug text-foreground mb-3">
            Good reviews. Steady bookings. No answers.
          </h2>
          <p className="text-sm leading-relaxed text-foreground mb-4">
            Running a few vacation rentals on the Mediterranean coast sounds straightforward.
            Guests book, guests arrive, revenue comes in. But beneath the surface, there are
            dozens of questions a standard PMS never answers.
          </p>
          <Quote>
            "I had a Lodgify dashboard that showed me bookings and a calendar. I had Airbnb reviews
            and Booking.com ratings. But I had no way to see the whole picture. Is my pricing right
            for this time of year? Are my reviews actually trending up or down? Is one property
            quietly dragging the portfolio? I was guessing."
          </Quote>
          <p className="text-sm leading-relaxed text-foreground">
            The problem isn't that data doesn't exist. Lodgify, Airbnb, and Booking.com all produce data.
            The problem is that none of it talks to each other, and none of it is organised around
            the question a host actually asks: "How is my property really doing, and what should I do about it?"
          </p>
        </section>

        <Separator />

        {/* ── Week One ── */}
        <section>
          <SectionLabel>Week One</SectionLabel>
          <h2 className="text-[17px] font-semibold leading-snug text-foreground mb-3">
            The dashboard that talks back
          </h2>
          <p className="text-sm leading-relaxed text-foreground mb-4">
            After connecting his Lodgify account — a process that took under ten minutes —
            Stayframe populated a property overview that immediately felt different from
            anything he'd used before.
          </p>
          <p className="text-sm leading-relaxed text-foreground mb-4">
            Instead of just showing a calendar and booking list, the overview opened with
            a stay readiness check — a real-time scorecard showing whether the current
            guest's experience was fully prepared: guidebook status, display content,
            house rules, access details, arrival information. Everything either marked
            ready or flagged for attention.
          </p>
          <Quote>
            "The first thing I noticed was the action rail. It didn't just show me data —
            it told me what to do. Ranked priorities with confidence levels. That was new."
          </Quote>
          <p className="text-sm leading-relaxed text-foreground">
            Below that, commercial metrics appeared that he'd never seen aggregated in
            one place: revenue versus comparison periods, occupancy pace, ADR trends,
            channel mix, lead times, cancellation risk. Not just numbers — context about
            what those numbers meant.
          </p>
        </section>

        <Separator />

        {/* ── Reputation ── */}
        <section>
          <SectionLabel>The Discovery</SectionLabel>
          <h2 className="text-[17px] font-semibold leading-snug text-foreground mb-3">
            The reputation score nobody else calculates
          </h2>
          <p className="text-sm leading-relaxed text-foreground mb-4">
            One feature stopped him in his tracks: the reputation intelligence page.
            Stayframe had pulled reviews from both Airbnb and Booking.com, analysed them,
            and produced a unified reputation score with a sentiment trend.
          </p>
          <p className="text-sm leading-relaxed text-foreground mb-4">
            But it went further. The system identified what guests were consistently
            praising — location, cleanliness, responsiveness — and what was dragging the
            score down. It even showed a loyalty signal: the percentage of reviews where
            guests described a stay they'd want to repeat.
          </p>

          <Card className="p-4 bg-card/80 border-border/50 my-6">
            <div className="flex items-start gap-3">
              <div className="h-8 w-8 rounded-md bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                <Wifi className="h-4 w-4 text-primary" />
              </div>
              <div>
                <p className="text-sm font-semibold text-foreground mb-1">The WiFi blind spot</p>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  Stayframe's review analysis revealed that WiFi reliability was being mentioned
                  negatively on both Airbnb and Booking.com — a pattern completely invisible when
                  reading reviews one platform at a time.
                </p>
              </div>
            </div>
          </Card>

          <Quote>
            "I knew guests mentioned WiFi sometimes. I didn't know it was a pattern showing
            up on both platforms. Stayframe connected dots I would never have connected on my own."
          </Quote>
        </section>

        <Separator />

        {/* ── Listing Audit ── */}
        <section>
          <SectionLabel>The Surprise</SectionLabel>
          <h2 className="text-[17px] font-semibold leading-snug text-foreground mb-3">
            The listing that was talking like the wrong season
          </h2>
          <p className="text-sm leading-relaxed text-foreground mb-4">
            The biggest surprise came from the listing intelligence page. Stayframe had
            audited the listing content and flagged something the host hadn't considered:
            the listing copy was emphasising summer activities, but shoulder season was
            approaching. The seasonal relevance score was dropping.
          </p>
          <p className="text-sm leading-relaxed text-foreground mb-4">
            More than that, it compared what guests actually praised in reviews against
            what the listing description highlighted. There were mismatches — guests
            consistently mentioned the garden and outdoor areas, but the listing photos
            underrepresented them. The system categorised every photo and identified coverage gaps.
          </p>
          <Quote>
            "It literally told me: your listing is talking like the wrong season, and it's
            not using your strongest guest-validated selling points. That's not a report —
            that's a coach."
          </Quote>
          <p className="text-sm leading-relaxed text-foreground">
            Stayframe provided AI-generated copy suggestions for each identified gap,
            ready to review and adapt. Not generic templates — suggestions built from
            actual guest feedback and seasonal demand patterns.
          </p>
        </section>

        <Separator />

        {/* ── Before / After ── */}
        <section>
          <SectionLabel>The Shift</SectionLabel>
          <h2 className="text-[17px] font-semibold leading-snug text-foreground mb-4">
            Before and after
          </h2>
          <div className="grid grid-cols-1 gap-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="text-xs font-semibold tracking-wider uppercase text-muted-foreground px-1 pb-1">
                Running on instinct
              </div>
              <div className="text-xs font-semibold tracking-wider uppercase text-primary px-1 pb-1">
                Running on intelligence
              </div>
            </div>
            {BEFORE_AFTER.map((row, i) => (
              <div key={i} className="grid grid-cols-2 gap-3">
                <div className="text-sm leading-relaxed text-muted-foreground bg-muted/50 rounded-md p-3">
                  {row.before}
                </div>
                <div className="text-sm leading-relaxed text-foreground bg-teal-50/50 rounded-md p-3 border border-primary/10">
                  {row.after}
                </div>
              </div>
            ))}
          </div>
        </section>

        <Separator />

        {/* ── Outcome ── */}
        <section>
          <SectionLabel>The Outcome</SectionLabel>
          <h2 className="text-[17px] font-semibold leading-snug text-foreground mb-3">
            Three changes in two weeks
          </h2>
          <p className="text-sm leading-relaxed text-foreground mb-4">
            Within the first two weeks, the host made three concrete changes based on Stayframe's intelligence:
          </p>
          <div className="space-y-3 mb-6">
            {[
              "Updated listing photos to better represent outdoor spaces and garden areas — the features guests praised most but the listing showcased least",
              "Refreshed listing copy to align with shoulder-season demand patterns instead of summer-oriented language",
              "Started investigating broadband upgrades after seeing WiFi flagged as a consistent friction point across both platforms",
            ].map((item, i) => (
              <div key={i} className="flex items-start gap-3">
                <CheckCircle2 className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" />
                <p className="text-sm leading-relaxed text-foreground">{item}</p>
              </div>
            ))}
          </div>
          <Quote>
            "I won't share numbers, but I'll say this: in four weeks I had more clarity about
            my properties than in years of hosting. The tool didn't just show me data. It showed
            me what to do with it."
          </Quote>
        </section>

        <Separator />

        {/* ── Key Takeaways ── */}
        <section>
          <Card className="bg-card/80 border-border/50 p-6 relative overflow-hidden">
            <div className="absolute top-0 left-0 w-1.5 h-full bg-primary rounded-l-lg" />
            <SectionLabel>Key Takeaways</SectionLabel>
            <div className="mt-3 space-y-3">
              {TAKEAWAYS.map((item, i) => (
                <div key={i} className="flex items-start gap-3">
                  <CheckCircle2 className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" />
                  <p className="text-sm leading-relaxed text-foreground">{item}</p>
                </div>
              ))}
            </div>
          </Card>
        </section>

        <Separator />

        {/* ── What's Inside ── */}
        <section>
          <SectionLabel>What's Inside</SectionLabel>
          <h2 className="text-[17px] font-semibold leading-snug text-foreground mb-4">
            Property intelligence for independent hosts
          </h2>
          <p className="text-sm leading-relaxed text-foreground mb-6">
            Stayframe is built for independent short-term rental hosts managing 1–15 properties.
            It's not a channel manager or booking engine — it's an intelligence layer that sits
            on top of your existing PMS and transforms scattered data into clear decisions.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {FEATURES.map((f) => (
              <Card key={f.name} className="p-4 bg-card/80 border-border/50">
                <div className="flex items-start gap-3">
                  <div className="h-8 w-8 rounded-md bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <f.icon className="h-4 w-4 text-primary" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-foreground mb-1">{f.name}</p>
                    <p className="text-xs text-muted-foreground leading-relaxed">{f.description}</p>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </section>

        <Separator />

        {/* ── CTA ── */}
        <section className="text-center py-8">
          <h2 className="text-[17px] font-semibold leading-snug text-foreground mb-2">
            Try it yourself
          </h2>
          <p className="text-sm text-muted-foreground mb-6 max-w-md mx-auto">
            Stayframe is in early access. Try it free for 14 days — no credit card required.
            Connect your Lodgify account and see what your data has been trying to tell you.
          </p>
          <div className="flex items-center justify-center gap-3">
            <Link to="/sign-up">
              <Button size="sm">
                Start free trial
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </Link>
            <Link to="/demo">
              <Button variant="outline" size="sm">
                <Eye className="h-4 w-4 mr-2" />
                See the demo first
              </Button>
            </Link>
          </div>
          <p className="text-xs text-muted-foreground mt-4">
            14 days free · No credit card · Setup in under 10 minutes
          </p>
        </section>
      </article>

      {/* ── Footer ── */}
      <footer className="border-t border-border/50 py-8">
        <div className="mx-auto max-w-3xl px-6 flex items-center justify-between">
          <span className="text-xs text-muted-foreground">
            © {new Date().getFullYear()} Stayframe
          </span>
          <Link to="/privacy" className="text-xs text-muted-foreground hover:text-primary transition-colors">
            Privacy Policy
          </Link>
        </div>
      </footer>
    </div>
  )
}
